"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MemberField = void 0;
const joi_1 = __importDefault(require("joi"));
const store_1 = require("../../exports/store");
const member_base_field_1 = require("./member_base_field");
const lodash_1 = require("lodash");
const utils_1 = require("../../model/utils");
const i18n_1 = require("../../exports/i18n");
class MemberField extends member_base_field_1.MemberBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    validateProperty() {
        return MemberField.propertySchema.validate(this.field.property);
    }
    validateCellValue(cellValue) {
        return MemberField.cellValueSchema.validate(cellValue);
    }
    validateOpenWriteValue(owv) {
        return MemberField.openWriteValueSchema.validate(owv);
    }
    get apiMetaProperty() {
        const unitMap = store_1.Selectors.getUnitMap(this.state);
        const options = [];
        if (unitMap) {
            this.field.property.unitIds.forEach(unitId => {
                if (unitMap.hasOwnProperty(unitId)) {
                    const { name, type, avatar } = unitMap[unitId];
                    options.push({
                        id: unitId,
                        name,
                        type: (0, utils_1.getMemberTypeString)(type),
                        avatar,
                    });
                }
            });
        }
        return {
            options,
            isMulti: this.field.property.isMulti,
            shouldSendMsg: this.field.property.shouldSendMsg,
            subscription: this.field.property.subscription,
        };
    }
    get openValueJsonSchema() {
        return {
            type: 'array',
            title: this.field.name,
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_member_IDs),
                    },
                    type: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_member_types),
                    },
                    name: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_member_names),
                    },
                    avatar: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_member_avatars),
                    },
                },
            }
        };
    }
    static polyfillOldData(cellValue) {
        if (!cellValue) {
            return cellValue;
        }
        if (!Array.isArray(cellValue)) {
            return null;
        }
        return cellValue.map(item => {
            if (typeof item === 'object') {
                // old data only returns unitId
                return item.unitId;
            }
            return item;
        });
    }
    static defaultProperty() {
        return {
            isMulti: true,
            shouldSendMsg: true,
            subscription: false,
            unitIds: [],
        };
    }
    recordEditable(datasheetId, mirrorId) {
        if (!super.recordEditable(datasheetId, mirrorId)) {
            return false;
        }
        const { templateId } = this.state.pageParams;
        return !templateId;
    }
    isMultiValueField() {
        return true;
    }
    stdValueToCellValue(stdValue) {
        // Match matching member information with name text in redux.
        const unitMap = store_1.Selectors.getUnitMap(this.state);
        if (!unitMap) {
            return null;
        }
        const unitValue = Object.values(unitMap);
        // The members of the space station may have the same name, so every time the data is converted, 
        // it is necessary to first find the members that are activated and not deleted, so all data must be checked
        const unitNames = Array.from(new Set(stdValue.data.map(d => d.text.split(/, ?/)).flat()));
        const cvMap = new Map();
        for (const name of unitNames) {
            for (const unit of unitValue) {
                if (unit.name !== name) {
                    continue;
                }
                cvMap.set(name, unit.unitId);
                if (unit.isActive && !unit.isDeleted) {
                    break;
                }
            }
        }
        return cvMap.size ? [...cvMap.values()] : null;
    }
    eq(cv1, cv2) {
        if (cv1 == null || cv2 == null) {
            return cv1 === cv2;
        }
        // Compatible with old data comparison
        return (0, lodash_1.isEqual)(MemberField.polyfillOldData([cv1].flat()), MemberField.polyfillOldData([cv2].flat()));
    }
    getUnitNames(cellValue) {
        const unitMap = store_1.Selectors.getUnitMap(this.state);
        if (!unitMap) {
            return null;
        }
        return cellValue.map(id => {
            // There is unitId in the cell, but there is no corresponding user information in the space. Two situations need to be compatible here:
            // 1. Old data
            // 2. Data pasted from other spaces, or data stored in the dumped table
            if (!unitMap[id]) {
                return '';
            }
            return unitMap[id].name;
        });
    }
    getUnitIds(cellValue) {
        return MemberField.polyfillOldData(cellValue);
    }
    getUnits(cellValue) {
        const unitMap = store_1.Selectors.getUnitMap(this.state);
        if (!unitMap) {
            return null;
        }
        return cellValue.map(unitId => {
            return unitMap[unitId];
        }).filter(item => item);
    }
    /**
     * API returns basic information of members
     * @param cellValues
     */
    cellValueToApiStandardValue(cellValues) {
        if ((0, lodash_1.isNil)(cellValues)) {
            return null;
        }
        const unitMap = store_1.Selectors.getUnitMap(this.state);
        if ((0, lodash_1.isNil)(unitMap)) {
            return null;
        }
        const units = [];
        cellValues.forEach(unitId => {
            var _a;
            if (unitMap.hasOwnProperty(unitId)) {
                units.push({
                    id: unitId,
                    type: (0, utils_1.getMemberTypeString)(unitMap[unitId].type),
                    name: unitMap[unitId].name,
                    avatar: (_a = unitMap[unitId]) === null || _a === void 0 ? void 0 : _a.avatar,
                });
            }
        });
        return units;
    }
    enrichProperty(stdVals) {
        const cellValues = stdVals.map(stdVal => {
            const cv = this.stdValueToCellValue(stdVal);
            if (!cv) {
                return [];
            }
            return cv;
        }).flat();
        return Object.assign(Object.assign({}, this.field.property), { unitIds: [...new Set(cellValues)] });
    }
    validate(value) {
        if (value == null) {
            return true;
        }
        if (Array.isArray(value)) {
            return value.every(unitId => {
                return typeof unitId === 'string';
            });
        }
        return false;
    }
    get openFieldProperty() {
        const unitMap = store_1.Selectors.getUnitMap(this.state);
        const options = [];
        if (unitMap) {
            this.field.property.unitIds.forEach(unitId => {
                if (unitMap.hasOwnProperty(unitId)) {
                    const { name, type, avatar } = unitMap[unitId];
                    options.push({
                        id: unitId,
                        name,
                        type: (0, utils_1.getMemberTypeString)(type),
                        avatar,
                    });
                }
            });
        }
        const { isMulti, shouldSendMsg, subscription } = this.field.property;
        return {
            options,
            isMulti,
            shouldSendMsg,
            subscription
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return MemberField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { isMulti, shouldSendMsg, subscription } = openFieldProperty;
        const { unitIds } = this.field.property;
        return {
            isMulti: Boolean(isMulti),
            shouldSendMsg: Boolean(shouldSendMsg),
            subscription: Boolean(subscription),
            unitIds
        };
    }
    addOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { isMulti, shouldSendMsg, subscription } = openFieldProperty;
        const defaultProperty = MemberField.defaultProperty();
        return {
            isMulti: isMulti !== null && isMulti !== void 0 ? isMulti : defaultProperty.isMulti,
            shouldSendMsg: shouldSendMsg !== null && shouldSendMsg !== void 0 ? shouldSendMsg : defaultProperty.shouldSendMsg,
            subscription: subscription !== null && subscription !== void 0 ? subscription : defaultProperty.subscription,
            unitIds: []
        };
    }
}
exports.MemberField = MemberField;
MemberField.propertySchema = joi_1.default.object({
    isMulti: joi_1.default.boolean().required(),
    unitIds: joi_1.default.array().items(joi_1.default.string()).required(),
    shouldSendMsg: joi_1.default.boolean(),
    subscription: joi_1.default.boolean(),
}).required();
MemberField.cellValueSchema = joi_1.default.array().items(joi_1.default.string().pattern(/^\d{10}/).required()).allow(null).required();
MemberField.openWriteValueSchema = joi_1.default.custom((owv, helpers) => {
    const memberIds = owv.map((v) => (0, lodash_1.isString)(v) ? v : v.id);
    if (!(memberIds === null || memberIds === void 0 ? void 0 : memberIds.length)) {
        return helpers.error('value format error');
    }
    for (const key of memberIds) {
        if (typeof key !== 'string') {
            return helpers.error(`memberId ${key} is must string`);
        }
    }
    return owv;
}).allow(null).required();
MemberField.openUpdatePropertySchema = joi_1.default.object({
    isMulti: joi_1.default.boolean(),
    shouldSendMsg: joi_1.default.boolean(),
    subscription: joi_1.default.boolean(),
}).required();
//# sourceMappingURL=member_field.js.map